/**
 * TWO KINDS OF EXPORT, and the header states both because it used to state only the first. `FEDERA_LOGO` and
 * `SENTINEL_PROPERTY_IMAGE` are FEDERA-facing names for two entries of the brand IMAGE registry
 * (`const/brand-assets.ts`), which is the SSOT for every ASSET decision -- path, intrinsic dimensions, alt text and
 * delivery channel. Both READ that registry; neither re-spells a path. The full rationale for each asset lives on its
 * registry entry (one paragraph, one place -- `QUAL-CROSS-FILE-COMMENT-PROSE-RUN-DUP-001` is what a mirrored copy
 * would trip).
 *
 * `OG_IMAGE_WIDTH` / `OG_IMAGE_HEIGHT` / `OG_IMAGE_SIZE` are the OTHER kind and are NOT registry reads: they are the
 * social-image OUTPUT CANVAS the routes RENDER at, not the intrinsic size of any file being served, which is why no
 * registry entry carries them -- `ogDefault` declares `width`/`height` as `null` and its own comment says why. THIS
 * LEAF IS THEIR SSOT and the header has to say so. It read "names for two entries ... Both consts below READ that
 * registry" while five consts sat below it, so a reader hunting where the OG canvas is decided was told this leaf
 * holds registry aliases and nothing else. Adoption is total today -- five leaves under `apps/federa/src/app/` import
 * the three, and no source hand-spells the magnitudes (measured 2026-09-03) -- and a header that under-describes its
 * own leaf is how that stops being true one hand-typed number at a time.
 *
 * These names are kept because they are what ~25 call sites already import and because they are BRAND-SCOPED spellings
 * of a brand-keyed registry; new renderers should prefer `components/brand/Logo.tsx` (which resolves the mark, its alt
 * text and its size from the registry in one place) over importing a bare url.
 *
 * THEY READ THE FEDERA MANIFEST, NEVER THE SELECTION, and the two consts' own names are why. This leaf lives INSIDE the
 * package that IS the brand named federa (`brand/federa/`, published as `@federa/design-system`), so it is a named-brand
 * leaf in the sense `const/brand-registry.ts` states on `ACTIVE_BRAND_NAME`: "A LEAF THAT *IS* A NAMED BRAND MUST NOT
 * READ THIS SELECTION". Reading `ACTIVE_BRAND_ASSETS` here would be the inversion `const/brand-assets.ts` records
 * refusing for its own federa manifest and `brand/federa/tokens/brand-manifest.ts` for its `brandName`: register a
 * second brand, flip `ACTIVE_BRAND_NAME`, and `FEDERA_LOGO` would export the OTHER brand's logo under a name that says
 * federa, with nothing red anywhere. A surface that genuinely wants whatever brand the checkout SELECTS reads
 * `ACTIVE_BRAND_ASSETS` (`const/brand-assets.ts`) directly, which is what `components/brand/Logo.tsx`,
 * `apps/federa/src/app/manifest.ts` and `utils/images/get-placeholder-image-url.ts` already do.
 */

import { BRAND_ASSETS } from '@federa/const/brand-assets';
import { BRAND_NAMES } from '@federa/const/brand-registry';

/** The imagery manifest of the brand this package IS, picked by NAME. `BRAND_ASSETS` is annotated total over the name
 *  record, so this lookup cannot resolve `undefined` -- the same guarantee `brand-manifest.ts` gets from `BRAND_NAMES.FEDERA`. */
const FEDERA_BRAND_ASSETS = BRAND_ASSETS[BRAND_NAMES.FEDERA];

/** Federa logo used in authentication and branding. Registry entry: `logoMark`. */
export const FEDERA_LOGO = FEDERA_BRAND_ASSETS.logoMark.src;

/** Sentinel image for properties without images (indicates "no data") -- a LOCAL asset, deliberately not a CDN one. Registry entry: `sentinelPropertyImage`, which carries the LCP history that made it local. */
export const SENTINEL_PROPERTY_IMAGE = FEDERA_BRAND_ASSETS.sentinelPropertyImage.src;

/** Open Graph image dimensions (Facebook/Twitter recommended). @see https://ogp.me/ */
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const OG_IMAGE_SIZE = {
	width: OG_IMAGE_WIDTH,
	height: OG_IMAGE_HEIGHT,
} as const;
